/**
 * @license
 * @copyright Copyright Motili Inc., 2024 All Rights Reserved
 */

import { useGridApiContext } from '@mui/x-data-grid-premium';
import React, { memo, useEffect, useRef, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import { styled } from 'styled-components';
import Config from 'Config';
import { generateRandomUUID } from 'common/utils/Utils';

interface DataGridOverflowTextProps {
    content: string;
    columnField: string;
    containerStyle?: React.CSSProperties;
    contentStyle?: React.CSSProperties;
    className?: 'dark' | 'info';
}

const DataGridOverflowText = memo(
    ({
        content,
        columnField,
        containerStyle,
        contentStyle,
        className,
    }: DataGridOverflowTextProps) => {
        const textRef = useRef<HTMLSpanElement>(null);
        const [textIsHidden, setIsTextHidden] = useState(false);
        const apiRef = useGridApiContext();
        const header = document.querySelector(
            `[role="columnheader"][data-field="${columnField}"]`
        ) as HTMLElement | null;

        useEffect(() => {
            if (!apiRef.current || !header) {
                return;
            }
            const observer = new ResizeObserver(() => {
                const columnWidth = apiRef.current.getColumn(columnField).width;
                const style = window.getComputedStyle(header);
                const paddingLeft = parseFloat(style.paddingLeft);
                const paddingRight = parseFloat(style.paddingRight);

                const textWidth =
                    textRef?.current?.getBoundingClientRect().width;
                if (!columnWidth || !textWidth) {
                    return;
                }
                setIsTextHidden(
                    textWidth > columnWidth - paddingLeft - paddingRight
                );
            });

            observer.observe(header);

            return () => observer.disconnect();
        }, [content]);
        const id = generateRandomUUID();
        return (
            <TextContainer
                data-tooltip-content={textIsHidden ? content : ''}
                data-tooltip-id={`tooltip-${columnField}-overflow-true-${id}`}
                style={containerStyle}
            >
                <span style={contentStyle} ref={textRef}>
                    {content}
                </span>
                <StyledTooltip
                    id={`tooltip-${columnField}-overflow-${textIsHidden}-${id}`}
                    variant='info'
                    place='right'
                    delayShow={1000}
                    disableStyleInjection='core'
                    className={className}
                />
            </TextContainer>
        );
    }
);

const TextContainer = styled.div`
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
`;

const StyledTooltip = styled(Tooltip)`
    &.dark {
        background: ${Config.color.darkGray3};
        opacity: 90%;
    }
    line-height: 16px;
`;

export default DataGridOverflowText;
